import { Package, PoundSterling, Clock, Truck, CheckCircle } from "lucide-react";

export interface OrderProgressStage {
  label: string;
  completed: boolean;
  date: string | null;
  icon: any;
}

/**
 * Standardized list of order workflow stages
 * This ensures consistency across all order displays
 */
export const ORDER_STAGES = [
  'created',
  'payment_processing', 
  'order_processing',
  'device_allocation',
  'fulfillment_started',
  'shipped',
  'delivered'
] as const;

/**
 * Compute order progress percentage based on current stage
 * @param order Order object with status and related data
 * @returns Progress percentage (0-100)
 */
export const computeOrderProgress = (order: any): number => {
  let currentStage = 0;

  // Stage 1: Order Created
  if (order.created_at) currentStage = 1;
  
  // Stage 2: Payment Processing - check both sale status and order status
  // Handle cases where sale data might not be available (e.g., for clients)
  const saleStatus = order.sale?.status;
  const orderStatus = order.status;
  
  if (saleStatus === 'paid' || 
      (orderStatus !== 'waiting_for_payment' && orderStatus !== 'pending')) {
    currentStage = 2;
  }
  
  // Stage 3: Order Processing
  if (orderStatus === 'order_processing' || 
      orderStatus === 'in_fulfillment' || 
      orderStatus === 'shipping' || 
      orderStatus === 'delivered') {
    currentStage = 3;
  }
  
  // Stage 4: Device Allocation (if applicable)
  if (order.devices_allocated_at || 
      (order.allocated_device_ids && order.allocated_device_ids.length > 0) ||
      order.skip_device_allocation ||
      orderStatus === 'in_fulfillment' || 
      orderStatus === 'shipping' || 
      orderStatus === 'delivered') {
    currentStage = 4;
  }
  
  // Stage 5: Fulfillment Started
  if (order.fulfillment_started_at || 
      orderStatus === 'in_fulfillment' || 
      orderStatus === 'shipping' || 
      orderStatus === 'delivered') {
    currentStage = 5;
  }
  
  // Stage 6: Shipped
  if ((order.shipped_at && order.tracking_number) || 
      orderStatus === 'delivered') {
    currentStage = 6;
  }
  
  // Stage 7: Delivered
  if (orderStatus === 'delivered') {
    currentStage = 7;
  }

  return (currentStage / ORDER_STAGES.length) * 100;
};

/**
 * Get detailed progress stages with completion status and timestamps
 * @param order Order object with status and related data
 * @returns Array of progress stages with completion info
 */
export const computeOrderStages = (order: any): OrderProgressStage[] => {
  const saleStatus = order.sale?.status;
  const orderStatus = order.status;
  
  return [
    { 
      label: 'Order Created', 
      completed: !!order.created_at,
      date: order.created_at,
      icon: Package
    },
    { 
      label: 'Payment Processing', 
      completed: saleStatus === 'paid' || 
                 (orderStatus !== 'waiting_for_payment' && orderStatus !== 'pending'),
      date: saleStatus === 'paid' ? order.sale?.updated_at : 
            (orderStatus !== 'waiting_for_payment' && orderStatus !== 'pending' ? order.updated_at : null),
      icon: PoundSterling
    },
    { 
      label: 'Order Processing', 
      completed: orderStatus === 'order_processing' || 
                 orderStatus === 'in_fulfillment' || 
                 orderStatus === 'shipping' || 
                 orderStatus === 'delivered',
      date: (orderStatus === 'order_processing' || 
             orderStatus === 'in_fulfillment' || 
             orderStatus === 'shipping' || 
             orderStatus === 'delivered') ? order.updated_at : null,
      icon: Clock
    },
    { 
      label: 'Device Allocation', 
      completed: !!order.devices_allocated_at || 
                 (order.allocated_device_ids && order.allocated_device_ids.length > 0) ||
                 !!order.skip_device_allocation ||
                 orderStatus === 'in_fulfillment' || 
                 orderStatus === 'shipping' || 
                 orderStatus === 'delivered',
      date: order.devices_allocated_at || 
            (order.skip_device_allocation ? order.updated_at : null),
      icon: Package
    },
    { 
      label: 'Fulfillment Started', 
      completed: !!order.fulfillment_started_at ||
                 orderStatus === 'in_fulfillment' || 
                 orderStatus === 'shipping' || 
                 orderStatus === 'delivered',
      date: order.fulfillment_started_at,
      icon: Clock
    },
    { 
      label: 'Shipped', 
      completed: (!!order.shipped_at && !!order.tracking_number) || 
                 orderStatus === 'delivered',
      date: order.shipped_at,
      icon: Truck
    },
    { 
      label: 'Delivered', 
      completed: orderStatus === 'delivered',
      date: orderStatus === 'delivered' ? order.updated_at : null,
      icon: CheckCircle
    }
  ];
};

/**
 * Get order status display color classes
 * @param status Order status
 * @returns CSS classes for status badge
 */
export const getOrderStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'waiting_for_payment': 
    case 'pending':
      return 'bg-red-100 text-red-800';
    case 'order_processing':
      return 'bg-blue-100 text-blue-800';
    case 'in_fulfillment':
      return 'bg-purple-100 text-purple-800';
    case 'shipping':
      return 'bg-indigo-100 text-indigo-800';
    case 'delivered': 
      return 'bg-green-100 text-green-800';
    default: 
      return 'bg-orange-100 text-orange-800';
  }
};

/**
 * Get progress bar color classes based on order status
 * @param status Order status
 * @returns CSS classes for progress bar
 */
export const getProgressBarColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'waiting_for_payment':
    case 'pending':
      return '[&>div]:bg-red-500';
    case 'delivered': 
      return '[&>div]:bg-green-500';
    default: 
      return '[&>div]:bg-orange-500';
  }
};